import { ChevronDownIcon } from "lucide-react";
import { Link } from "react-router-dom";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/base/Collapsible";
import { cn } from "@/lib/utils";
import { ReactNode, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/base/Sheet.tsx";
import { useScreenSizes } from "@/hooks/useScreenSizes.ts";

export type LinkItem = {
  label: string;
  icon?: ReactNode;
  href: string;
};

export type CollapsibleSection = {
  title: string;
  icon?: ReactNode;
  linkItems: Array<LinkItem>;
  onItemClick?: () => void;
};

interface SidebarProps {
  linkItems: Array<Array<LinkItem | CollapsibleSection>>;
  isCollapsed?: boolean;
  setIsCollapsed?: (collapsed: boolean) => void;
}

const LinkItem = ({
  link,
  subLink,
  onClick,
}: {
  link: LinkItem;
  subLink?: boolean;
  onClick?: () => void;
}) => {
  return (
    <li className="w-full my-1 flex flex-row">
      <Link to={link.href} className="flex flew-grow w-full">
        <button
          onClick={() => (onClick ? onClick() : null)}
          className={cn(
            "px-2 py-2 w-full text-left flex flex-row items-center border-b",
            subLink && "ml-4"
          )}
        >
          {link.icon}
          {link.label}
        </button>
      </Link>
    </li>
  );
};

const CollapsibleItem = (section: CollapsibleSection) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Collapsible
      open={isOpen}
      onOpenChange={setIsOpen}
      className={cn(!isOpen && "border-b")}
    >
      <CollapsibleTrigger className="px-2 py-2 w-full text-left rounded-md flex flex-row items-center justify-between">
        <div className="flex flex-row items-center">
          {section.icon}
          {section.title}
        </div>
        <ChevronDownIcon
          className={cn("transition duration-300", isOpen && "rotate-180")}
        />
      </CollapsibleTrigger>
      <CollapsibleContent>
        {section.linkItems.map((item, iIndex) => (
          <LinkItem
            key={`${section.title}-${iIndex}`}
            link={item}
            subLink
            onClick={section.onItemClick}
          />
        ))}
      </CollapsibleContent>
    </Collapsible>
  );
};

export const NavDrawer = ({
  linkItems,
  isCollapsed,
  setIsCollapsed,
}: SidebarProps) => {
  const { isMd } = useScreenSizes();
  return (
    <>
      {!isMd && (
        <div className="md:hidden">
          <Sheet open={isCollapsed} onOpenChange={setIsCollapsed}>
            <SheetContent side="left">
              <SheetHeader>
                <SheetTitle>RouteMe Software</SheetTitle>
                <SheetDescription>
                  {linkItems?.map((section, sIndex) => (
                    <ul key={sIndex} className="w-full">
                      {section.map((item, iIndex) => {
                        if ("title" in item) {
                          return (
                            <CollapsibleItem
                              key={iIndex}
                              {...item}
                              onItemClick={() =>
                                setIsCollapsed ? setIsCollapsed(false) : null
                              }
                            />
                          );
                        } else {
                          return (
                            <LinkItem
                              key={iIndex}
                              link={item}
                              onClick={() =>
                                setIsCollapsed ? setIsCollapsed(false) : null
                              }
                            />
                          );
                        }
                      })}
                    </ul>
                  ))}
                </SheetDescription>
              </SheetHeader>
            </SheetContent>
          </Sheet>
        </div>
      )}

      <nav
        data-collapsed={isCollapsed}
        className="p-2 w-48 bg-primary text-primary-foreground text-sm data-[collapsed=true]:hidden hidden md:flex"
      >
        {linkItems?.map((section, sIndex) => (
          <ul key={sIndex} className="w-full">
            {section.map((item, iIndex) => {
              if ("title" in item) {
                return <CollapsibleItem key={iIndex} {...item} />;
              } else {
                return <LinkItem key={iIndex} link={item} />;
              }
            })}
          </ul>
        ))}
      </nav>
    </>
  );
};
